import React from "react";
import {
  BarChart3, CalendarClock, Clock, Code2, HelpCircle, Layers, RefreshCw, Shield, XCircle,
} from "lucide-react";
import { AppCard, AppCardContent, Callout } from "../ui";
import {
  MODE_LABELS, formatSchedule, plural, sectionContents, sectionsOf,
} from "./studentAssessmentRules";

/** One fact about the assessment — an icon and a few words. */
function Detail({ icon: Icon, tone, children }) {
  return (
    <div className={tone || "tw-text-content-muted"}>
      <span className="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm">
        <Icon size={16} aria-hidden="true" className="tw-shrink-0 tw-text-primary" />
        {children}
      </span>
    </div>
  );
}

/**
 * AssessmentBriefing — what a student reads before starting: the facts, the
 * sections (and whether they run in order on their own timers), coding, and the
 * instructions, with the start button underneath.
 *
 * One card for two pages: the student's own `/s/assessment/:id`, and a staff
 * member's preview of it, so a preview can never show a briefing the student
 * does not get. `assessment` is the student info read's shape (the preview
 * endpoint answers with the same one); `action` is the button row.
 */
export default function AssessmentBriefing({ assessment: a, action }) {
  const now = new Date();
  const notStartedYet = a.startDate && new Date(a.startDate) > now;
  const sections = sectionsOf(a);
  const sequential = a.sectionNavigation === "sequential" && sections.length > 1;
  const codingCount = sections.reduce((n, s) => n + s.coding, 0);

  return (
    <AppCard as="article" aria-labelledby="assessment-title">
      <AppCardContent className="tw-flex tw-flex-col tw-gap-5">
        <div className="tw-flex tw-flex-col tw-gap-1.5">
          <h1 id="assessment-title" className="tw-m-0 tw-text-[1.375rem] tw-font-semibold tw-tracking-tight tw-text-content">{a.title}</h1>
          {a.description && <p className="tw-m-0 tw-text-sm tw-text-content-muted">{a.description}</p>}
        </div>

        <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
          <Detail icon={HelpCircle}>{plural(a.questionCount || 0, "question")}</Detail>
          <Detail icon={Clock}>{a.timeLimit > 0 ? `${a.timeLimit} min` : "No time limit"}</Detail>
          <Detail icon={RefreshCw}>{a.attemptsUsed || 0}/{a.maxAttempts > 0 ? a.maxAttempts : "∞"} attempts used</Detail>
          <Detail icon={BarChart3}>{a.passingPercentage > 0 ? `${a.passingPercentage}% to pass` : "No pass mark"}</Detail>
          <Detail icon={Shield}>Mode: {MODE_LABELS[a.mode] || "Practice"}</Detail>
          {a.startDate && (
            <Detail icon={CalendarClock} tone={notStartedYet ? "tw-text-warning-fg" : undefined}>Opens {formatSchedule(a.startDate)}</Detail>
          )}
          {a.endDate && <Detail icon={CalendarClock}>Closes {formatSchedule(a.endDate)}</Detail>}
          {a.negativeMarking && <Detail icon={XCircle} tone="tw-text-danger-fg">Negative marking — a wrong answer loses marks</Detail>}
        </div>

        {sections.length > 1 && (
          <section aria-labelledby="sections-title" className="tw-flex tw-flex-col tw-gap-2">
            <h2 id="sections-title" className="tw-m-0 tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-content">
              <Layers size={16} aria-hidden="true" className="tw-text-primary" /> Sections
            </h2>
            <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-0">
              {sections.map((s, i) => (
                <li key={s.key} className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-px-3 tw-py-2.5 tw-text-sm tw-border-0 tw-border-t tw-border-solid tw-border-line first:tw-border-t-0">
                  <span className="tw-font-semibold tw-text-content-muted">{String.fromCodePoint(65 + i)}.</span>
                  <span className="tw-min-w-0 tw-flex-1 tw-font-medium tw-text-content">{s.title}</span>
                  <span className="tw-text-[0.8125rem] tw-text-content-muted">
                    {sectionContents(s)}{s.minutes ? ` · ${s.minutes} min` : ""}
                  </span>
                </li>
              ))}
            </ol>
            {sequential && (
              <Callout tone="info">
                Sections run in order, each on its own timer; a finished section cannot be reopened.
              </Callout>
            )}
          </section>
        )}

        {codingCount > 0 && (
          <Callout tone="info" icon={<Code2 size={15} />}>
            Coding problems: write and run code; checked against hidden tests when you submit.
          </Callout>
        )}

        {a.instructions && (
          <div className="tw-whitespace-pre-wrap tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-sunken tw-p-4 tw-text-[0.8125rem] tw-leading-relaxed tw-text-content">
            {a.instructions}
          </div>
        )}

        {action && <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-3">{action}</div>}
      </AppCardContent>
    </AppCard>
  );
}
